import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { PageHeader, Card, Loading, EmptyState } from "@/components/common";
import { WorkflowBadge, VisibilityBadge } from "@/components/insights/InsightStatusBadges";
import { WORKFLOW, fmtDateTime } from "@/lib/insightsApi";
import { FileStack, Plus, Search } from "lucide-react";

export default function Insights() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [types, setTypes] = useState([]);
  const [depts, setDepts] = useState([]);
  const [error, setError] = useState("");

  const f = {
    q: params.get("q") || "",
    insight_type_id: params.get("insight_type_id") || "",
    department_id: params.get("department_id") || "",
    workflow_status: params.get("workflow_status") || "",
    visibility: params.get("visibility") || "",
    mine: params.get("mine") === "1",
  };

  useEffect(() => {
    api.get("/insight-types").then((r) => setTypes(r.data.insight_types)).catch(() => {});
    api.get("/departments").then((r) => setDepts(r.data.departments)).catch(() => {});
  }, []);

  useEffect(() => {
    setData(null); setError("");
    const qs = new URLSearchParams({ limit: "100" });
    Object.entries(f).forEach(([k, v]) => { if (v && v !== false) qs.set(k, k === "mine" ? "1" : v); });
    api.get(`/insights?${qs.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setError("We couldn't load Insights right now. Please try again."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const setF = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="insights-page">
      <PageHeader
        title="Insights"
        subtitle="Search and track authoritative Insight records across your authorized scope."
        actions={hasMinRole(user?.role, "analyst") && (
          <Link to="/insights/new" data-testid="insights-create-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-900 transition-colors">
            <Plus className="h-4 w-4" /> New Insight
          </Link>
        )}
      />

      <Card className="mb-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="relative sm:col-span-2 lg:col-span-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input data-testid="insights-filter-q" defaultValue={f.q}
              onKeyDown={(e) => { if (e.key === "Enter") setF("q", e.target.value); }}
              onBlur={(e) => setF("q", e.target.value)} placeholder="Search title or summary…"
              aria-label="Search Insights"
              className="w-full h-10 rounded-lg border border-brand-line bg-brand-bg pl-9 pr-3 text-sm text-brand-ink focus:bg-white focus:border-brand-civic outline-none" />
          </div>
          <FilterSelect testid="insights-filter-type" label="Type" value={f.insight_type_id}
            onChange={(v) => setF("insight_type_id", v)}
            options={types.map((t) => [t.type_id, t.name])} />
          <FilterSelect testid="insights-filter-dept" label="Department" value={f.department_id}
            onChange={(v) => setF("department_id", v)}
            options={depts.map((d) => [d.department_id, d.name])} />
          <FilterSelect testid="insights-filter-workflow" label="Workflow" value={f.workflow_status}
            onChange={(v) => setF("workflow_status", v)}
            options={Object.entries(WORKFLOW).map(([k, v]) => [k, v.label])} />
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="insights-filter-mine">
          <input type="checkbox" checked={f.mine} onChange={(e) => setF("mine", e.target.checked ? "1" : "")}
            className="h-4 w-4 rounded border-brand-line text-brand-civic focus:ring-brand-civic" />
          Only mine
        </label>
      </Card>

      {error ? <EmptyState icon={FileStack} title="Unable to load" description={error} />
        : !data ? <Loading label="Loading Insights…" />
        : data.insights.length === 0
          ? <EmptyState icon={FileStack} title="No Insights found" description="Try adjusting your filters, or create a new Insight." />
          : (
            <Card className="p-0 overflow-hidden">
              <table className="w-full text-sm" data-testid="insights-table">
                <thead>
                  <tr className="border-b border-brand-line bg-brand-bg text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-semibold">Title</th>
                    <th className="px-4 py-3 font-semibold hidden md:table-cell">Type</th>
                    <th className="px-4 py-3 font-semibold">Workflow</th>
                    <th className="px-4 py-3 font-semibold hidden sm:table-cell">Visibility</th>
                    <th className="px-4 py-3 font-semibold hidden lg:table-cell">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {data.insights.map((i) => (
                    <tr key={i.insight_id} data-testid="insight-row"
                      className="border-b border-brand-line/60 last:border-0 hover:bg-brand-bg/60 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/insights/${i.insight_id}`} className="font-semibold text-brand-navy hover:text-brand-civic">
                          {i.title}
                        </Link>
                        <div className="text-xs text-slate-400 md:hidden">{i.insight_type_code}</div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{i.insight_type_code}</td>
                      <td className="px-4 py-3"><WorkflowBadge status={i.workflow_status} /></td>
                      <td className="px-4 py-3 hidden sm:table-cell"><VisibilityBadge visibility={i.visibility} /></td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">{fmtDateTime(i.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 text-xs text-slate-400 border-t border-brand-line" data-testid="insights-total">
                {data.total} Insight{data.total === 1 ? "" : "s"} in your authorized scope
              </div>
            </Card>
          )}
    </div>
  );
}

function FilterSelect({ testid, label, value, onChange, options }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}
      className="h-10 rounded-lg border border-brand-line bg-white px-3 text-sm text-brand-ink focus:border-brand-civic outline-none">
      <option value="">{`All ${label}`}</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
